/**
 * HierarchyBreadcrumb — Phase 4 of the UX v2 redesign.
 *
 * Plan reference: /root/.claude/plans/lexical-pondering-stonebraker.md
 *                 §"Phase 4 — Portfolios, Programs, lineage repair".
 *
 * Renders Portfolio > Program > Project on every project-scoped page
 * (Principle 3 — persistent hierarchy breadcrumbs). Empty levels are
 * hidden so legacy orgs without portfolios/programs see just the
 * Project label — no broken "/ /" rendering.
 *
 * Resolution path:
 *   project.program_id → /api/v1/programs/{id} (gives portfolio_id + name)
 *                      → /api/v1/portfolios/{id} (gives name)
 *
 * Each level is a Link to that level's detail page (when the v2 flag
 * for that page is on) or plain text (when not, so we don't dangle
 * users at a 404).
 *
 * Patent surface: none. Pure read-only navigation primitive.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { apiClient } from "@/api/client";
import { cn } from "@/lib/utils";
import { recordTelemetry } from "@/lib/telemetry";

// Inline types — the canonical exports live in `@/api/services` as
// PR #753 lands them. Keeping local copies here lets this primitive
// ship independently of the API-client merge order.

interface _Project {
  id: string;
  name: string;
  program_id: string | null;
}

interface _Program {
  id: string;
  portfolio_id: string;
  name: string;
}

interface _Portfolio {
  id: string;
  name: string;
}

export interface HierarchyBreadcrumbProps {
  projectId: string;
  /** Optional project name override — avoids a refetch when the host
   *  page already has the row in hand. */
  projectName?: string;
  className?: string;
}

/** Fetches the project (just for program_id + name) when no override is
 *  supplied. */
function useProject(projectId: string, hasOverride: boolean) {
  return useQuery<_Project>({
    queryKey: ["v2-breadcrumb-project", projectId],
    queryFn: () => apiClient.get<_Project>(`/projects/${projectId}`),
    enabled: !!projectId && !hasOverride,
    staleTime: 60_000,
  });
}

function useProgram(programId: string | null | undefined) {
  return useQuery<_Program | null>({
    queryKey: ["v2-breadcrumb-program", programId],
    queryFn: async () => {
      if (!programId) return null;
      try {
        return await apiClient.get<_Program>(`/programs/${programId}`);
      } catch {
        // Endpoint not yet deployed OR program soft-deleted — fall back
        // to the project-only crumb rather than blocking the page.
        return null;
      }
    },
    enabled: !!programId,
    staleTime: 5 * 60_000,
  });
}

function usePortfolio(portfolioId: string | null | undefined) {
  return useQuery<_Portfolio | null>({
    queryKey: ["v2-breadcrumb-portfolio", portfolioId],
    queryFn: async () => {
      if (!portfolioId) return null;
      try {
        return await apiClient.get<_Portfolio>(`/portfolios/${portfolioId}`);
      } catch {
        return null;
      }
    },
    enabled: !!portfolioId,
    staleTime: 5 * 60_000,
  });
}

export function HierarchyBreadcrumb({
  projectId,
  projectName,
  className,
}: HierarchyBreadcrumbProps) {
  const hasOverride = !!projectName;
  const projectQ = useProject(projectId, hasOverride);
  const project = hasOverride
    ? ({ id: projectId, name: projectName!, program_id: null } as _Project)
    : projectQ.data;

  const programQ = useProgram(project?.program_id ?? null);
  const portfolioQ = usePortfolio(programQ.data?.portfolio_id ?? null);

  // While the project hasn't loaded, render a quiet placeholder so the
  // layout doesn't jump.
  if (!project) {
    if (projectQ.isLoading) {
      return (
        <div
          className={cn(
            "flex items-center gap-1 text-xs text-muted-foreground",
            className,
          )}
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Loading hierarchy…</span>
        </div>
      );
    }
    return null;
  }

  const crumbs: Array<{ key: string; label: string; to?: string }> = [];

  if (portfolioQ.data) {
    crumbs.push({
      key: "portfolio",
      label: portfolioQ.data.name,
      to: `/portfolios/${portfolioQ.data.id}`,
    });
  }
  if (programQ.data) {
    crumbs.push({
      key: "program",
      label: programQ.data.name,
      to: `/programs/${programQ.data.id}`,
    });
  }
  crumbs.push({
    key: "project",
    label: project.name,
    to: `/projects/${projectId}`,
  });

  // No portfolio + no program → render nothing rather than a redundant
  // single-Project crumb (the page header already shows the project name).
  if (crumbs.length === 1) {
    return null;
  }

  return (
    <nav
      aria-label="Hierarchy"
      className={cn(
        "flex items-center gap-1 text-xs text-muted-foreground",
        className,
      )}
      data-testid="v2-hierarchy-breadcrumb"
    >
      {crumbs.map((c, i) => (
        <span key={c.key} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3 opacity-60" />}
          {c.to ? (
            <Link
              to={c.to}
              onClick={() => {
                // Plan §Verification — hierarchy navigation engagement.
                void recordTelemetry("hierarchy_breadcrumb_clicked", {
                  level: c.key,
                  depth: i,
                });
              }}
              className="hover:text-foreground transition-colors truncate max-w-[min(45vw,280px)]"
              data-testid={`v2-breadcrumb-${c.key}`}
              title={c.label}
            >
              {c.label}
            </Link>
          ) : (
            // title= surfaces the full label on hover for the rare crumb still
            // long enough to truncate at the widened cap (holistic-review fix:
            // 160px clipped ordinary project names).
            <span className="truncate max-w-[min(45vw,280px)]" title={c.label}>
              {c.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
